import React from 'react';
import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { formatMoneyLocalized } from '../../utils/currency';
import { formatDate } from '../../utils/dateUtils';
import { localeForLanguage } from '../../utils/numberFormatting';

const { Text } = Typography;

interface TransportAllocatedCostProps {
  allocatedTransportCost?: number | null;
  originalCurrency?: string | null;
  originalAmount?: number | null;
  exchangeRateUsed?: number | null;
  exchangeRateSource?: string | null;
  exchangeRateTableNumber?: string | null;
  exchangeRateRequestedDate?: string | null;
  exchangeRateEffectiveDate?: string | null;
  exchangeRateIsFallback?: boolean | null;
  provenanceClassName?: string;
}

/** Canonical PLN first; optional provenance comes only from the saved Cost snapshot. */
export const TransportAllocatedCost: React.FC<TransportAllocatedCostProps> = ({
  allocatedTransportCost,
  originalCurrency,
  originalAmount,
  exchangeRateUsed,
  exchangeRateSource,
  exchangeRateTableNumber,
  exchangeRateRequestedDate,
  exchangeRateEffectiveDate,
  exchangeRateIsFallback,
  provenanceClassName,
}) => {
  const { t, i18n } = useTranslation(['transport']);
  const normalizedCurrency = String(originalCurrency || '').toUpperCase();
  const hasSavedForeignProvenance = normalizedCurrency !== ''
    && normalizedCurrency !== 'PLN'
    && originalAmount != null
    && Number.isFinite(Number(originalAmount))
    && exchangeRateUsed != null
    && Number.isFinite(Number(exchangeRateUsed))
    && Number(exchangeRateUsed) > 0;
  const formattedRate = hasSavedForeignProvenance
    ? Number(exchangeRateUsed).toLocaleString(localeForLanguage(i18n.language), {
        minimumFractionDigits: 4,
        maximumFractionDigits: 6,
      })
    : null;
  const provenanceSourceLabel = exchangeRateSource
    ? t(`transport:allocation.costProvenanceSources.${exchangeRateSource}`, {
        defaultValue: exchangeRateSource,
      })
    : t('transport:allocation.costProvenanceUnknownSource');
  const provenanceDetails = !hasSavedForeignProvenance
    ? null
    : exchangeRateSource === 'LEGACY_SAVED_RATE'
      ? t('transport:allocation.costProvenanceLegacy')
      : t('transport:allocation.costProvenanceDetails', {
          date: exchangeRateEffectiveDate ? formatDate(exchangeRateEffectiveDate) : t('transport:allocation.costProvenanceUnknownDate'),
          source: provenanceSourceLabel,
          table: exchangeRateTableNumber || t('transport:allocation.costProvenanceNoTable'),
        });
  const requestedDateContext = hasSavedForeignProvenance
    && exchangeRateRequestedDate
    && exchangeRateRequestedDate !== exchangeRateEffectiveDate
      ? t('transport:allocation.costProvenanceRequestedDate', {
          date: formatDate(exchangeRateRequestedDate),
        })
      : null;

  return (
    <span data-testid="transport-allocated-cost">
      {formatMoneyLocalized(Number(allocatedTransportCost || 0), 'PLN', i18n.language)}
      {hasSavedForeignProvenance ? (
        <Text type="secondary" className={provenanceClassName} style={{ marginLeft: 6 }}>
          ({t('transport:allocation.costProvenance', {
            original: formatMoneyLocalized(Number(originalAmount), normalizedCurrency, i18n.language),
            rate: formattedRate,
          })}{provenanceDetails ? ` · ${provenanceDetails}` : ''}{requestedDateContext ? ` · ${requestedDateContext}` : ''}{exchangeRateIsFallback ? ` · ${t('transport:allocation.costProvenanceFallback')}` : ''})
        </Text>
      ) : null}
    </span>
  );
};
